import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../Layout";
import {
  Box,
  Paper,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  LinearProgress,
} from "@mui/material";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

import { getVoyages } from "../../../data/voyage";
import { getSelectedTerminalCalls } from "../../../data/selectedterminalcalls";
import { normalizeIds } from "../../utils/normalize";

dayjs.extend(weekOfYear);

type DepartureStats = {
  total_teu?: number;
  total_weight?: number;
};

type DepartureState = {
  statistics?: DepartureStats;
};

interface VesselLike {
  barge?: boolean;
  name?: string;
}

interface TerminalCallLike {
  "@id"?: string;
  id?: number | string;
  planned?: string;
  vessel?: VesselLike;
  departureState?: DepartureState;
  departure_state?: DepartureState;
  bargeDepartureState?: DepartureState;
  bargedepartureState?: DepartureState;
}

function getYear(date: string | undefined | null): number | null {
  if (!date) return null;

  const d = dayjs(date);

  return d.isValid() ? d.year() : null;
}

function getWeek(date: string | undefined): number | null {
  if (!date) return null;

  const d = dayjs(date);

  return d.isValid() ? d.week() : null;
}

function getMonth(date: string | undefined): number | null {
  if (!date) return null;
  const d = dayjs(date);
  return d.isValid() ? d.month() : null;
}

function getDaysInMonth(year: number, monthIndex0: number): number {
  return dayjs().year(year).month(monthIndex0).daysInMonth();
}

function pickDepartureState(tc: TerminalCallLike): DepartureState | undefined {
  const isBarge = !!tc.vessel?.barge;

  if (isBarge) {
    return (
      tc.bargeDepartureState ||
      (tc as any).barge_departure_state ||
      tc.bargedepartureState
    );
  }

  return (
    tc.departureState ||
    (tc as any).departure_state ||
    (tc as any).departure_state
  );
}

function extractStatsFromTerminalCall(tc: TerminalCallLike): {
  totalTeu: number;
  totalWeight: number;
  isBarge: boolean;
} {
  const isBarge = !!tc.vessel?.barge;
  const state = pickDepartureState(tc);

  const totalTeu =
    (state?.statistics as any)?.total_teu ??
    (state?.statistics as any)?.totalTeu ??
    0;
  const totalWeight =
    (state?.statistics as any)?.total_weight ??
    (state?.statistics as any)?.totalWeight ??
    0;

  return { totalTeu, totalWeight, isBarge };
}

type WeeklyRow = {
  week: number;
  teuPerWeek: number;
  maxWeight: number;
  maxTeu: number;
};

type MonthlyRow = {
  month: number;
  monthLabel: string;
  teuPerMonth: number;
  avgTeuPerDay: number;
  maxWeight: number;
  maxTeu: number;
};

function buildWeeklyData(
  terminalCalls: TerminalCallLike[],
  year: number,
  includeVessels: boolean,
  includeBarges: boolean
): WeeklyRow[] {
  const map = new Map<number, WeeklyRow>();

  for (const tc of terminalCalls) {
    const y = getYear(tc.planned);
    if (y !== year) continue;

    const week = getWeek(tc.planned);
    if (!week) continue;

    const { totalTeu, totalWeight, isBarge } = extractStatsFromTerminalCall(tc);

    if (isBarge && !includeBarges) continue;
    if (!isBarge && !includeVessels) continue;

    const existing = map.get(week) || {
      week,
      teuPerWeek: 0,
      maxWeight: 0,
      maxTeu: 0,
    };

    existing.teuPerWeek += totalTeu;
    existing.maxWeight = Math.max(existing.maxWeight, totalWeight);
    existing.maxTeu = Math.max(existing.maxTeu, totalTeu);

    map.set(week, existing);
  }

  return Array.from(map.values()).sort((a, b) => a.week - b.week);
}

function buildMonthlyData(
  terminalCalls: TerminalCallLike[],
  year: number,
  includeVessels: boolean,
  includeBarges: boolean
): MonthlyRow[] {
  const map = new Map<number, Omit<MonthlyRow, "monthLabel" | "avgTeuPerDay">>();

  for (const tc of terminalCalls) {
    const y = getYear(tc.planned);
    if (y !== year) continue;

    const month = getMonth(tc.planned);
    if (month === null) continue;

    const { totalTeu, totalWeight, isBarge } = extractStatsFromTerminalCall(tc);

    if (isBarge && !includeBarges) continue;
    if (!isBarge && !includeVessels) continue;

    const existing = map.get(month) || {
      month,
      teuPerMonth: 0,
      maxWeight: 0,
      maxTeu: 0,
    };

    existing.teuPerMonth += totalTeu;
    existing.maxWeight = Math.max(existing.maxWeight, totalWeight);
    existing.maxTeu = Math.max(existing.maxTeu, totalTeu);

    map.set(month, existing);
  }

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const rows: MonthlyRow[] = Array.from(map.values())
    .sort((a, b) => a.month - b.month)
    .map((row) => {
      const daysInMonth = getDaysInMonth(year, row.month);
      const avgTeuPerDay =
        daysInMonth > 0 ? row.teuPerMonth / daysInMonth : 0;

      return {
        ...row,
        monthLabel: monthNames[row.month],
        avgTeuPerDay,
      };
    });

  return rows;
}

const VolumeReport: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getVoyages());
  }, [dispatch]);

  const voyagesState = useSelector((state: any) => state.voyages);
  const voyages = voyagesState?.data ?? [];

  const selectedTerminalCalls = useSelector(
    (state: any) => state.selectedTerminalCalls
  );
  const allTerminalCalls: TerminalCallLike[] = useMemo(
    () => selectedTerminalCalls?.data ?? [],
    [selectedTerminalCalls]
  );

  const currentYear = dayjs().year();

  const yearOptions = useMemo(
    () => [currentYear - 2, currentYear - 1, currentYear].reverse(),
    [currentYear]
  );

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [includeVessels, setIncludeVessels] = useState(true);
  const [includeBarges, setIncludeBarges] = useState(true);

  useEffect(() => {
    if (!voyages.length) return;

    const activeVoyagesForYear = voyages.filter((v: any) => {
      if (v.status !== "active") return false;

      const y = getYear(v.startDate);

      return y === selectedYear;
    });

    const allTerminalCallIris = activeVoyagesForYear.flatMap(
      (v: any) => v.terminalCalls ?? []
    );

    if (!allTerminalCallIris.length) {

      return;
    }

    const terminalCallIds = normalizeIds(allTerminalCallIris);
    dispatch(getSelectedTerminalCalls(terminalCallIds));
  }, [voyages, selectedYear, dispatch]);

  const weeklyData = useMemo(
    () =>
      buildWeeklyData(
        allTerminalCalls,
        selectedYear,
        includeVessels,
        includeBarges
      ),
    [allTerminalCalls, selectedYear, includeVessels, includeBarges]
  );

  const monthlyData = useMemo(
    () =>
      buildMonthlyData(
        allTerminalCalls,
        selectedYear,
        includeVessels,
        includeBarges
      ),
    [allTerminalCalls, selectedYear, includeVessels, includeBarges]
  );

  const loadingVoyages = voyagesState?.loading;
  const loadingTerminalCalls = selectedTerminalCalls?.loading;

  const loading = loadingVoyages || loadingTerminalCalls;

  return (
    <Layout>
      {loading && (
        <Box
          sx={{
            width: "100%",
            mt: 0.5,
          }}
        >
          <LinearProgress
            sx={{
              height: 3,
              borderRadius: 9999,
            }}
          />
        </Box>
      )}

      <Box p={2} display="flex" flexDirection="column" gap={3}>
        <Paper sx={{ p: 2, display: "flex", gap: 3, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="volume-report-year-label">Year</InputLabel>
            <Select
              labelId="volume-report-year-label"
              label="Year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value as number)}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeVessels}
                  onChange={(e) => setIncludeVessels(e.target.checked)}
                />
              }
              label="Vessel"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeBarges}
                  onChange={(e) => setIncludeBarges(e.target.checked)}
                />
              }
              label="Barge"
            />
          </FormGroup>
        </Paper>

        {!loading && !voyages.length && (
          <Alert severity="info">No voyages found.</Alert>
        )}

        {!loading && voyages.length > 0 && weeklyData.length === 0 && (
          <Alert severity="info">
            No active voyages with terminal calls found for {selectedYear}.
          </Alert>
        )}

        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6">Section 1: Per week</Typography>

          <Divider />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Week</TableCell>
                <TableCell align="right">TEU per week</TableCell>
                <TableCell align="right">Max weight</TableCell>
                <TableCell align="right">Max TEU</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weeklyData.map((row) => (
                <TableRow key={row.week}>
                  <TableCell>{row.week}</TableCell>
                  <TableCell align="right">
                    {row.teuPerWeek.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    {row.maxWeight.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    {row.maxTeu.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
              {weeklyData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No weekly data for {selectedYear}.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Box sx={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="week"
                  label={{
                    value: "Week",
                    position: "insideBottom",
                    offset: -5,
                  }}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="teuPerWeek"
                  name="TEU per week"
                  fill="#4f6f8f"
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6">Section 2: Per month</Typography>

          <Divider />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell align="right">TEU per month</TableCell>
                <TableCell align="right">Average TEU / day</TableCell>
                <TableCell align="right">Max weight</TableCell>
                <TableCell align="right">Max TEU</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthlyData.map((row) => (
                <TableRow key={row.month}>
                  <TableCell>{row.monthLabel}</TableCell>
                  <TableCell align="right">
                    {row.teuPerMonth.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    {row.avgTeuPerDay.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    {row.maxWeight.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    {row.maxTeu.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
              {monthlyData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No monthly data for {selectedYear}.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Box sx={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="monthLabel" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="teuPerMonth"
                  name="TEU per month"
                  fill="#4f6f8f"
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>
    </Layout>
  );
};

export default VolumeReport;